import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { PageHero, Spinner } from "@/components/common/Bits";
import { useLanguage, pick } from "@/context/LanguageContext";
import { Calendar } from "lucide-react";

const localeFor = (lang) => lang === "en" ? "en-US" : (lang === "ar" ? "ar-SA" : "tr-TR");

export function NewsListPage() {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/news?limit=50").then(r => setItems(r.data)).finally(() => setLoading(false)); }, []);

  return (
    <div data-testid="page-news">
      <PageHero overline={t.ov_news} title={t.latest_news} description={t.news_hero_desc}/>
      <section className="py-16 useko-container">
        {loading ? <div className="py-16 text-center"><Spinner/></div> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map(n => (
              <Link key={n.id} to={`/haberler/${n.slug}`} className="useko-card overflow-hidden group" data-testid={`news-item-${n.slug}`}>
                {n.cover_image && (
                  <div className="aspect-[16/10] overflow-hidden bg-[#051436] flex items-center justify-center">
                    <img src={fileUrl(n.cover_image)} alt="" className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"/>
                  </div>
                )}
                <div className="p-6">
                  <div className="text-xs text-[#4B5563] flex items-center gap-1.5 mb-2"><Calendar className="w-3.5 h-3.5"/>{new Date(n.published_at).toLocaleDateString(localeFor(lang))}</div>
                  <h3 className="font-heading text-lg font-bold text-[#102A43] leading-snug line-clamp-2">{pick(n, "title", lang)}</h3>
                  <p className="mt-2 text-sm text-[#4B5563] line-clamp-3">{pick(n, "summary", lang)}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function NewsDetailPage() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const [n, setN] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => { api.get(`/news/${slug}`).then(r => setN(r.data)).catch(() => setNotFound(true)); }, [slug]);

  if (notFound) return (
    <div className="useko-container py-32 text-center">
      <h1 className="font-heading text-3xl font-bold text-[#102A43]">{t.not_found_title}</h1>
      <Link to="/haberler" className="btn-primary mt-6 inline-flex">{t.back}</Link>
    </div>
  );
  if (!n) return <div className="py-32 text-center"><Spinner/></div>;
  return (
    <article data-testid={`page-news-${slug}`}>
      {n.cover_image && (
        <div className="relative w-full bg-[#051436] flex items-center justify-center" style={{ minHeight: "320px", maxHeight: "600px" }}>
          <img src={fileUrl(n.cover_image)} alt="" className="w-full h-auto max-h-[600px] object-contain"/>
        </div>
      )}
      <div className="useko-container max-w-3xl py-16 overflow-hidden">
        <div className="overline mb-3">{new Date(n.published_at).toLocaleDateString(localeFor(lang))}</div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#102A43] tracking-tight leading-tight break-words">{pick(n, "title", lang)}</h1>
        <p className="mt-5 text-lg text-[#4B5563] leading-relaxed break-words">{pick(n, "summary", lang)}</p>
        <div className="mt-10 prose-useko rich-content" dangerouslySetInnerHTML={{ __html: pick(n, "content", lang) || "" }}/>
      </div>
    </article>
  );
}

export function AnnouncementsListPage() {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/announcements?limit=50").then(r => setItems(r.data)).catch(()=>{}); }, []);
  return (
    <div data-testid="page-announcements">
      <PageHero overline={t.ov_announcements} title={t.latest_announcements} description={t.announcements_hero_desc}/>
      <section className="py-16 useko-container max-w-3xl space-y-3">
        {items.map(a => (
          <Link key={a.id} to={`/duyurular/${a.slug}`} className="block p-6 useko-card" data-testid={`announcement-item-${a.slug}`}>
            <div className="text-xs uppercase tracking-widest text-[#1F8A5B] font-medium">{new Date(a.published_at).toLocaleDateString(localeFor(lang))}</div>
            <h3 className="mt-1 font-heading text-lg font-bold text-[#102A43]">{pick(a, "title", lang)}</h3>
            <p className="mt-2 text-sm text-[#4B5563] line-clamp-2">{pick(a, "summary", lang)}</p>
          </Link>
        ))}
      </section>
    </div>
  );
}

export function AnnouncementDetailPage() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const [a, setA] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => { api.get(`/announcements/${slug}`).then(r => setA(r.data)).catch(() => setNotFound(true)); }, [slug]);
  if (notFound) return <div className="useko-container py-32 text-center"><h1 className="font-heading text-3xl font-bold text-[#102A43]">{t.not_found_title}</h1><Link to="/duyurular" className="btn-primary mt-6 inline-flex">{t.back}</Link></div>;
  if (!a) return <div className="py-32 text-center"><Spinner/></div>;
  return (
    <div className="useko-container max-w-3xl py-20 overflow-hidden" data-testid={`page-ann-${slug}`}>
      <div className="overline mb-3">{new Date(a.published_at).toLocaleDateString(localeFor(lang))}</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#102A43] tracking-tight leading-tight break-words">{pick(a, "title", lang)}</h1>
      <p className="mt-5 text-lg text-[#4B5563] break-words">{pick(a, "summary", lang)}</p>
      <div className="mt-10 prose-useko rich-content" dangerouslySetInnerHTML={{ __html: pick(a, "content", lang) || "" }}/>
    </div>
  );
}
